import {CAMPAIGN_KEYS} from "@/api/campaignApi";
import {useInvalidateChatNeedsReply} from "@/api/chatApi";
import type {ContentSubmissionSerializer} from "@/generated/DjangoTypes";
import {client} from "@/httpClient";
import {capturePostHogEvent, PostHogEvents} from "@/posthog";
import useCampaignInfluencerFilterStore from "@/store/campaignInfluencerFilter";
import {useManagementCampaign} from "@/store/campaignManagement";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";

export const SUBMISSION_KEYS = {
  mAcceptSubmission: (submissionId: number) => [`/submissions/${submissionId}/accept/`],
  mOverrideSubmission: (submissionId: number) => [`/submissions/${submissionId}/override/`],
  mRejectSubmission: (submissionId: number) => [`/submissions/${submissionId}/reject/`],
  mRequestRevision: (submissionId: number) => [`/submissions/${submissionId}/request_revision/`],
  mRevertToRevision: (submissionId: number) => [`/submissions/${submissionId}/revert_to_revision/`],
  qSubmission: (submissionId: number) => [`/submissions/${submissionId}/`],
} as const;

export const useRevertToRevisionMutation = (submissionId: number) => {
  return useMutation({
    mutationKey: SUBMISSION_KEYS.mRevertToRevision(submissionId),
    mutationFn: ({message}: {message: string}) =>
      client.post(SUBMISSION_KEYS.mRevertToRevision(submissionId)[0], {message}),
  });
};

export const useSubmissionQuery = (submissionId: number | null) =>
  useQuery<ContentSubmissionSerializer>({
    queryKey: SUBMISSION_KEYS.qSubmission(submissionId!),
    queryFn: async () => (await client.get(SUBMISSION_KEYS.qSubmission(submissionId!)[0])).data,
    enabled: !!submissionId,
  });

export const useOverrideSubmissionMutation = (submissionId: number) => {
  const queryClient = useQueryClient();
  const campaign = useManagementCampaign();
  const search = useCampaignInfluencerFilterStore(state => state.filters.search);
  const invalidateChatNeedsReply = useInvalidateChatNeedsReply();

  return useMutation({
    mutationKey: SUBMISSION_KEYS.mOverrideSubmission(submissionId),
    mutationFn: () => client.post(SUBMISSION_KEYS.mOverrideSubmission(submissionId)[0]),
    onSuccess: () => {
      [
        CAMPAIGN_KEYS.qCampaignActiveApplications(campaign.id, search),
        CAMPAIGN_KEYS.qCampaignManagement(campaign.id),
      ].map(queryKey => queryClient.invalidateQueries({queryKey}));
      invalidateChatNeedsReply();
    },
  });
};

export const useAcceptSubmissionMutation = ({
  caption,
  id,
  onSuccess,
}: {
  caption?: string;
  id: number;
  onSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();
  const campaign = useManagementCampaign();
  const search = useCampaignInfluencerFilterStore(state => state.filters.search);
  const invalidateChatNeedsReply = useInvalidateChatNeedsReply();

  return useMutation({
    mutationKey: SUBMISSION_KEYS.mAcceptSubmission(id),
    mutationFn: () =>
      client.post(SUBMISSION_KEYS.mAcceptSubmission(id)[0], {
        caption,
      }),
    onSuccess: () => {
      [
        CAMPAIGN_KEYS.qCampaignActiveApplications(campaign.id, search),
        CAMPAIGN_KEYS.qCampaignManagement(campaign.id),
      ].map(queryKey => queryClient.invalidateQueries({queryKey}));
      invalidateChatNeedsReply();

      capturePostHogEvent(PostHogEvents.ContentApproved, {campaignId: campaign.id, submissionId: id});

      onSuccess?.();
    },
  });
};
